// ---------------------------------------------------------------------------
// HowToModalV3 - "Centro de Ajuda" da tela de Inputs V3.
// ---------------------------------------------------------------------------
// Guia how-to voltado ao usuario (analista de negocio). Layout de
// "central de ajuda": indice lateral a esquerda + conteudo a direita.
// Disparado pelo botao "?" da ``InputsV3GlobalTopBar``.
//
// Todo o conteudo vem do i18n (``inputs_v3.howto.*``) para manter
// traducao e revisao centralizadas. As secoes que pedem enfase usam a
// convencao ``**negrito**`` dentro das strings (ver ``renderRich``),
// evitando HTML em arquivo de traducao e mantendo o texto legivel para
// quem traduz.
// ---------------------------------------------------------------------------

import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";

import StarBorder from "@material-ui/icons/StarBorder";
import SyncAlt from "@material-ui/icons/SyncAlt";
import CloudUpload from "@material-ui/icons/CloudUpload";
import ReportProblemOutlined from "@material-ui/icons/ReportProblemOutlined";
import HelpOutline from "@material-ui/icons/HelpOutline";
import QuestionAnswer from "@material-ui/icons/QuestionAnswer";
import Search from "@material-ui/icons/Search";

import ModalHeader from "../../../components/ModalHeader";
import useThemeColors from "../../../hooks/useThemeColors";
import {
  blendColors,
  grayColor,
} from "../../../assets/jss/material-dashboard-pro-react";

// Barra de rolagem fina, transparente, que so aparece no hover - mesmo
// padrao usado nas listas roláveis da InputsV3 (ver ``useOverlayScrollStyles``
// em ``inputs_v3_components/styles.js``).
const overlayScroll = {
  scrollbarWidth: "thin",
  scrollbarColor: "transparent transparent",
  "&:hover": { scrollbarColor: grayColor[6] + " transparent" },
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "transparent",
    borderRadius: "3px",
    transition: "background 0.2s ease",
  },
  "&:hover::-webkit-scrollbar-thumb": { background: grayColor[6] },
};

// Ordem canonica das secoes + icone de cada uma. Os textos (titulo do
// item de menu + subtitulo) vem do i18n por chave derivada do ``key``.
const SECTIONS = [
  { key: "ch0", Icon: StarBorder },
  { key: "flow", Icon: SyncAlt },
  { key: "upload", Icon: CloudUpload },
  { key: "points", Icon: ReportProblemOutlined },
  { key: "pend", Icon: HelpOutline },
  { key: "faq", Icon: QuestionAnswer },
];

// Converte ``**negrito**`` em <strong>. Mantem o restante como texto.
function renderRich(text) {
  if (text == null) return null;
  const parts = String(text).split("**");
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i}>{part}</strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
}

function normalize(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 8,
    overflow: "hidden",
    maxWidth: 1120,
    width: "100%",
  },
  shell: {
    display: "flex",
    minHeight: 0,
    height: "min(74vh, 600px)",
    fontFamily: theme?.typography?.fontFamily,
  },
  // ===== Indice lateral =====
  rail: {
    flex: "0 0 250px",
    background: "#f7f8fa",
    borderRight: "1px solid #eceff1",
    padding: "14px 10px",
    overflowY: "auto",
    ...overlayScroll,
  },
  navBtn: {
    display: "flex",
    alignItems: "flex-start",
    gap: 9,
    width: "100%",
    textAlign: "left",
    border: "none",
    background: "none",
    padding: "9px 10px",
    borderRadius: 6,
    cursor: "pointer",
    color: "#5b6b73",
    fontFamily: "inherit",
    lineHeight: 1.25,
    marginBottom: 2,
    transition: "background 120ms, color 120ms",
    "&:hover": { background: "#eef1f4" },
    "& svg": { width: 17, height: 17, flexShrink: 0, marginTop: 1 },
  },
  navBtnActive: {
    background: "#ffffff",
    boxShadow: "0 1px 2px rgba(38,50,56,0.06)",
    color: (props) => props.clientColor,
    "&:hover": { background: "#ffffff" },
  },
  navFirst: {
    border: "1px dashed #cfd8dc",
    marginBottom: 8,
  },
  navLbl: { minWidth: 0 },
  navTitle: { fontSize: 12.5, fontWeight: 600, display: "block" },
  navSub: { fontSize: 10.5, color: "#90a4ae", display: "block" },
  // ===== Corpo =====
  body: {
    flex: 1,
    minWidth: 0,
    overflowY: "auto",
    padding: "24px 30px",
    ...overlayScroll,
  },
  eyebrow: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "#607d8b",
    fontWeight: 700,
  },
  h3: { margin: "2px 0 4px", fontSize: 19, fontWeight: 700, color: "#2f3b41" },
  h5: { margin: "18px 0 6px", fontSize: 13, fontWeight: 700, color: "#2f3b41" },
  lede: {
    margin: "0 0 16px",
    fontSize: 13.5,
    color: "#5b6b73",
    lineHeight: 1.55,
  },
  p: { fontSize: 12.5, color: "#5b6b73", lineHeight: 1.55, margin: "0 0 10px" },
  note: { fontSize: 11.5, color: "#90a4ae", margin: "8px 0 0" },
  step: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 14,
  },
  stepNum: {
    flex: "0 0 26px",
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: (props) => blendColors("#ffffff", props.clientColor, 0.85),
    color: (props) => props.clientColor,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
  },
  stepTitle: {
    margin: "2px 0 2px",
    fontSize: 13,
    fontWeight: 600,
    color: "#2f3b41",
  },
  stepBody: { margin: 0, fontSize: 12.5, color: "#5b6b73", lineHeight: 1.5 },
  badge: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.04em",
    padding: "1px 6px",
    borderRadius: 4,
    textTransform: "uppercase",
    marginLeft: 6,
    verticalAlign: "middle",
  },
  badgeYou: {
    background: (props) => blendColors("#ffffff", props.clientColor, 0.85),
    color: (props) => props.clientColor,
  },
  badgeAuto: { background: "#e3f2fd", color: "#1976d2" },
  callout: {
    display: "flex",
    gap: 10,
    background: "#e3f2fd",
    color: "#0d47a1",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 12,
    lineHeight: 1.5,
    margin: "14px 0",
    "& svg": { width: 16, height: 16, flexShrink: 0, marginTop: 1 },
  },
  calloutTip: {
    background: (props) => blendColors("#ffffff", props.clientColor, 0.9),
    color: "#37474f",
  },
  calloutWarn: { background: "#fff3e0", color: "#8a5300" },
  pills: { display: "flex", gap: 10, flexWrap: "wrap", margin: "12px 0" },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 999,
  },
  pillOk: { background: "#e8f5e9", color: "#43a047" },
  pillWarn: { background: "#fff3e0", color: "#fb8c00" },
  pillCrit: { background: "#ffebee", color: "#f44336" },
  dimTags: { display: "flex", gap: 6, flexWrap: "wrap", margin: "8px 0" },
  dimTag: {
    background: "#eceff1",
    borderRadius: 999,
    padding: "3px 10px",
    fontSize: 11,
    color: "#607d8b",
  },
  pnl: {
    width: "100%",
    maxWidth: 360,
    borderCollapse: "collapse",
    margin: "10px 0 4px",
    fontSize: 12,
    "& td": { padding: "5px 8px", borderBottom: "1px solid #eceff1" },
    "& td:last-child": {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      color: "#5b6b73",
    },
  },
  pnlTotal: {
    "& td": { fontWeight: 700, color: "#2f3b41", borderBottom: "none" },
  },
  // ===== FAQ =====
  search: {
    position: "relative",
    margin: "4px 0 16px",
    "& svg": {
      position: "absolute",
      left: 10,
      top: "50%",
      transform: "translateY(-50%)",
      width: 18,
      height: 18,
      color: "#90a4ae",
    },
  },
  searchInput: {
    width: "100%",
    padding: "9px 12px 9px 36px",
    border: "1px solid #cfd8dc",
    borderRadius: 8,
    fontSize: 13,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    "&:focus": {
      borderColor: (props) => props.clientColor,
      boxShadow: (props) =>
        "0 0 0 3px " + blendColors("#ffffff", props.clientColor, 0.82),
    },
  },
  faqItem: { padding: "12px 0", borderBottom: "1px solid #eceff1" },
  faqQ: { fontWeight: 700, margin: "0 0 3px", fontSize: 13, color: "#2f3b41" },
  faqA: { color: "#5b6b73", margin: 0, fontSize: 12.5, lineHeight: 1.5 },
  faqEmpty: { color: "#90a4ae", fontSize: 12.5, padding: "16px 0" },
}));

// Renderiza um cartao de destaque (callout) com variante de cor.
function Callout({ classes, variant, children }) {
  const cls = [
    classes.callout,
    variant === "tip" ? classes.calloutTip : null,
    variant === "warn" ? classes.calloutWarn : null,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={cls}>
      <HelpOutline />
      <div>{children}</div>
    </div>
  );
}
Callout.propTypes = {
  classes: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(["info", "tip", "warn"]),
  children: PropTypes.node,
};

// Lista de passos numerados/alfabeticos com badge opcional (you/auto).
function Steps({ classes, t, items, marker }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <>
      {list.map((step, i) => {
        const label = marker === "alpha" ? String.fromCharCode(65 + i) : i + 1;
        return (
          <div className={classes.step} key={i}>
            <div className={classes.stepNum}>{label}</div>
            <div>
              <h4 className={classes.stepTitle}>
                {step.title}
                {step.badge === "you" && (
                  <span className={`${classes.badge} ${classes.badgeYou}`}>
                    {t("inputs_v3.howto.badge_you")}
                  </span>
                )}
                {step.badge === "auto" && (
                  <span className={`${classes.badge} ${classes.badgeAuto}`}>
                    {t("inputs_v3.howto.badge_auto")}
                  </span>
                )}
              </h4>
              <p className={classes.stepBody}>{renderRich(step.body)}</p>
            </div>
          </div>
        );
      })}
    </>
  );
}
Steps.propTypes = {
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  items: PropTypes.array,
  marker: PropTypes.oneOf(["num", "alpha"]),
};

export default function HowToModalV3({ open, onClose, Transition }) {
  const { t } = useTranslation();
  const { clientColor } = useThemeColors();
  const classes = useStyles({ clientColor });
  const [active, setActive] = useState("ch0");
  const [query, setQuery] = useState("");

  const tObj = (key) => t(`inputs_v3.howto.${key}`, { returnObjects: true });

  const faqItems = useMemo(() => {
    const items = t("inputs_v3.howto.faq.items", { returnObjects: true });
    return Array.isArray(items) ? items : [];
  }, [t]);

  const filteredFaq = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return faqItems;
    return faqItems.filter(
      (it) => normalize(`${it.q} ${it.a}`).indexOf(q) !== -1,
    );
  }, [faqItems, query]);

  const renderPane = () => {
    switch (active) {
      case "ch0": {
        const dims = tObj("ch0.dims");
        const pnl = tObj("ch0.pnl");
        const total = tObj("ch0.pnl_total");
        return (
          <div>
            <span className={classes.eyebrow}>
              {t("inputs_v3.howto.ch0.eyebrow")}
            </span>
            <h3 className={classes.h3}>{t("inputs_v3.howto.ch0.heading")}</h3>
            <p className={classes.lede}>{t("inputs_v3.howto.ch0.lede")}</p>

            <h5 className={classes.h5}>
              {t("inputs_v3.howto.ch0.cts_heading")}
            </h5>
            <p className={classes.p}>
              {renderRich(t("inputs_v3.howto.ch0.cts_body"))}
            </p>

            <h5 className={classes.h5}>
              {t("inputs_v3.howto.ch0.deliver_heading")}
            </h5>
            <p className={classes.p}>
              {renderRich(t("inputs_v3.howto.ch0.deliver_body"))}
            </p>
            <div className={classes.dimTags}>
              {(Array.isArray(dims) ? dims : []).map((d, i) => (
                <span className={classes.dimTag} key={i}>
                  {d}
                </span>
              ))}
            </div>
            <table className={classes.pnl}>
              <tbody>
                {(Array.isArray(pnl) ? pnl : []).map((row, i) => (
                  <tr key={i}>
                    <td>{row.label}</td>
                    <td>{row.value}</td>
                  </tr>
                ))}
                {total && (
                  <tr className={classes.pnlTotal}>
                    <td>{total.label}</td>
                    <td>{total.value}</td>
                  </tr>
                )}
              </tbody>
            </table>
            <p className={classes.note}>{t("inputs_v3.howto.ch0.pnl_note")}</p>

            <Callout classes={classes} variant="info">
              {renderRich(t("inputs_v3.howto.ch0.callout"))}
            </Callout>
          </div>
        );
      }
      case "flow": {
        return (
          <div>
            <span className={classes.eyebrow}>
              {t("inputs_v3.howto.flow.eyebrow")}
            </span>
            <h3 className={classes.h3}>{t("inputs_v3.howto.flow.heading")}</h3>
            <p className={classes.lede}>{t("inputs_v3.howto.flow.lede")}</p>
            <Steps
              classes={classes}
              t={t}
              items={tObj("flow.steps")}
              marker="num"
            />
            <Callout classes={classes} variant="info">
              {renderRich(t("inputs_v3.howto.flow.status_callout"))}
            </Callout>
            <h5 className={classes.h5}>
              {t("inputs_v3.howto.flow.yours_heading")}
            </h5>
            <p className={classes.p}>
              <span className={`${classes.badge} ${classes.badgeYou}`}>
                {t("inputs_v3.howto.badge_you")}
              </span>{" "}
              {t("inputs_v3.howto.flow.yours")}
              <br />
              <span className={`${classes.badge} ${classes.badgeAuto}`}>
                {t("inputs_v3.howto.badge_auto")}
              </span>{" "}
              {t("inputs_v3.howto.flow.system")}
            </p>
          </div>
        );
      }
      case "upload": {
        return (
          <div>
            <span className={classes.eyebrow}>
              {t("inputs_v3.howto.upload.eyebrow")}
            </span>
            <h3 className={classes.h3}>
              {t("inputs_v3.howto.upload.heading")}
            </h3>
            <p className={classes.lede}>{t("inputs_v3.howto.upload.lede")}</p>
            <Steps
              classes={classes}
              t={t}
              items={tObj("upload.steps")}
              marker="num"
            />
            <h5 className={classes.h5}>
              {t("inputs_v3.howto.upload.shortcuts_heading")}
            </h5>
            <Callout classes={classes} variant="tip">
              {renderRich(t("inputs_v3.howto.upload.reuse"))}
              <br />
              {renderRich(t("inputs_v3.howto.upload.optional"))}
              <br />
              {renderRich(t("inputs_v3.howto.upload.continuous"))}
            </Callout>
            <h5 className={classes.h5}>
              {t("inputs_v3.howto.upload.follow_heading")}
            </h5>
            <p className={classes.p}>
              {t("inputs_v3.howto.upload.follow_body")}
            </p>
            <p className={classes.note}>
              {t("inputs_v3.howto.upload.permission_note")}
            </p>
          </div>
        );
      }
      case "points": {
        return (
          <div>
            <span className={classes.eyebrow}>
              {t("inputs_v3.howto.points.eyebrow")}
            </span>
            <h3 className={classes.h3}>
              {t("inputs_v3.howto.points.heading")}
            </h3>
            <p className={classes.lede}>{t("inputs_v3.howto.points.lede")}</p>
            <div className={classes.pills}>
              <span className={`${classes.pill} ${classes.pillOk}`}>
                {t("inputs_v3.howto.state_ok")}
              </span>
              <span className={`${classes.pill} ${classes.pillWarn}`}>
                {t("inputs_v3.howto.state_attention")}
              </span>
              <span className={`${classes.pill} ${classes.pillCrit}`}>
                {t("inputs_v3.howto.state_critical")}
              </span>
            </div>
            <p className={classes.p}>
              {renderRich(t("inputs_v3.howto.points.legend_note"))}
            </p>
            <Steps
              classes={classes}
              t={t}
              items={tObj("points.steps")}
              marker="num"
            />
            <Callout classes={classes} variant="warn">
              {renderRich(t("inputs_v3.howto.points.callout"))}
            </Callout>
          </div>
        );
      }
      case "pend": {
        return (
          <div>
            <span className={classes.eyebrow}>
              {t("inputs_v3.howto.pend.eyebrow")}
            </span>
            <h3 className={classes.h3}>{t("inputs_v3.howto.pend.heading")}</h3>
            <p className={classes.lede}>
              {renderRich(t("inputs_v3.howto.pend.lede"))}
            </p>
            <Steps
              classes={classes}
              t={t}
              items={tObj("pend.steps")}
              marker="num"
            />
            <Callout classes={classes} variant="info">
              {renderRich(t("inputs_v3.howto.pend.callout"))}
            </Callout>
          </div>
        );
      }
      case "faq": {
        return (
          <div>
            <span className={classes.eyebrow}>
              {t("inputs_v3.howto.faq.eyebrow")}
            </span>
            <h3 className={classes.h3}>{t("inputs_v3.howto.faq.heading")}</h3>
            <div className={classes.search}>
              <Search />
              <input
                type="text"
                className={classes.searchInput}
                placeholder={t("inputs_v3.howto.faq.search_placeholder")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            {filteredFaq.length === 0 ? (
              <p className={classes.faqEmpty}>
                {t("inputs_v3.howto.faq.empty")}
              </p>
            ) : (
              filteredFaq.map((it, i) => (
                <div className={classes.faqItem} key={i}>
                  <p className={classes.faqQ}>{it.q}</p>
                  <p className={classes.faqA}>{renderRich(it.a)}</p>
                </div>
              ))
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      classes={{ paper: classes.paper }}
      fullWidth
      maxWidth="lg"
      aria-labelledby="inputs-v3-howto-title"
    >
      <ModalHeader
        id="inputs-v3-howto-title"
        title={t("inputs_v3.howto.title")}
        subtitle={t("inputs_v3.howto.subtitle")}
        onClose={onClose}
        closeLabel={t("common.close")}
      />
      <div className={classes.shell}>
        <nav className={classes.rail} aria-label={t("inputs_v3.howto.title")}>
          {SECTIONS.map(({ key, Icon }, idx) => {
            const isActive = active === key;
            const cls = [
              classes.navBtn,
              isActive ? classes.navBtnActive : null,
              idx === 0 ? classes.navFirst : null,
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <button
                type="button"
                key={key}
                className={cls}
                onClick={() => setActive(key)}
              >
                <Icon />
                <span className={classes.navLbl}>
                  <span className={classes.navTitle}>
                    {t(`inputs_v3.howto.nav.${key}_title`)}
                  </span>
                  <span className={classes.navSub}>
                    {t(`inputs_v3.howto.nav.${key}_sub`)}
                  </span>
                </span>
              </button>
            );
          })}
        </nav>
        <div className={classes.body}>{renderPane()}</div>
      </div>
    </Dialog>
  );
}

HowToModalV3.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  Transition: PropTypes.any,
};
